// Resolve canvas (editor) + workspace (intranet shell) origins for the
// CURRENT channel — single source of truth lives in
// `server/packages/channels/channels.json` via `@symbo.ls/channels`.
// Handles every shape the user can land on:
//
//   shell host        — `<tier>.my.symbols.app`              (channels.shell)
//   preview host      — `<tier>.preview.symbols.app`         (channels.preview)
//   project preview   — `<owner>--<key>.<tier>.preview.symbols.app`
//                       (suffix-match — same tier as bare preview)
//   localhost variants — *.localhost / localhost             (`local` channel)
//
// The standalone canvas hosts (`edit.symbols.app`, `canvas.<tier>.symbols.app`)
// are deleted (2026-10-23/24): the editor IS the shell. Use these instead of
// hardcoding `https://my.symbols.app/…` anywhere a surface needs to deep-link
// into the matching tier's editor or workspace shell.
//
// FA204 / frank trap — every export below used to share a bank of
// module-scope helpers (_hostnameOf/_channelOf/_isLocalHost/_localOrigin/
// _canvasOriginForChannel) plus bare sibling-export calls (canvasUrl ->
// canvasOrigin, workspaceUrl -> workspaceOrigin). Unresolvable once each
// export is individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers). The `@symbo.ls/channels`
// imports (channelShellUrl/channelPreviewUrl/channelNames) are NOT part of
// this trap — cross-file ESM imports resolve fine via frank's globalScope
// fallback; only same-file module-scope locals and bare sibling reads break.
// Each export below now carries its own self-contained inline copy of
// exactly the local helpers it calls.

import {
  shellUrl as channelShellUrl,
  previewUrl as channelPreviewUrl,
  channelNames,
  channelByApiUrl,
  resolveChannel
} from '@symbo.ls/channels'

// Resolve the active channel from the current browser hostname instead of
// relying on `@symbo.ls/channels`'s `currentChannel()` — that one falls
// back to `defaultChannel = production` whenever NODE_ENV isn't a known
// channel name (e.g. CI builds use `NODE_ENV=dev`, but the registry's
// canonical name is `development`). For browser surfaces the hostname IS
// the source of truth, so any caller that needs the channel for URL
// composition (mermaidUrl, previewUrl, …) should reach through this
// helper. Returns `null` on the server side or when no match — let the
// caller decide the fallback.
export const currentChannelFromHost = () => {
  function _hostnameOf(url) {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }
  function _channelOf(host) {
    if (!host) return null
    const tiers = []
    for (const name of channelNames) {
      for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
        const h = _hostnameOf(url)
        if (h) tiers.push({ name, h })
      }
    }
    tiers.sort((a, b) => b.h.length - a.h.length)
    // Two channels can serve ONE host: `demo` and `production` share the prod
    // preview host (and `demo-dev`/`development` the dev one). First-in-
    // registry-order named `demo` for every prod preview page, so its shell
    // links went to demo.symbols.app. The registry's own collision rule
    // (`channelByApiUrl` — a real plane outranks its demo twin) breaks the tie.
    const pick = (names) =>
      names.find((n) => channelByApiUrl(resolveChannel(n).api) === n) || names[0]
    const exact = tiers.filter(({ h }) => host === h)
    if (exact.length) return pick(exact.map(({ name }) => name))
    const suffix = tiers.filter(({ h }) => host.endsWith('.' + h))
    if (suffix.length) {
      const best = suffix[0].h.length
      return pick(suffix.filter(({ h }) => h.length === best).map(({ name }) => name))
    }
    return null
  }
  function _isLocalHost(host) {
    return !!host && (host === 'localhost' || host.endsWith('.localhost'))
  }

  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  if (_isLocalHost(host)) return 'local'
  return _channelOf(host)
}

// Canvas (editor) origin — the WORKSPACE SHELL of the current channel.
//
// 2026-10-21, per Nika: "remove independent canvas deployments ... purely rely
// on workspace". The editor runs ONLY inside the shell, at the shell's own
// project route (`/<owner>/<key>`, `/w/<org>/p/<key>` — the shell catch-all,
// workspace/pages/projectRedirect.js). So the canvas origin IS the shell
// origin: on a shell host it is this same origin; on a preview host it is
// that channel's shell. The retired canvas hosts (`edit.symbols.app`,
// `canvas.<tier>.symbols.app`) are deleted (2026-10-23/24) — no page is served
// from them, so `_channelOf` no longer classifies them.
export const canvasOrigin = () => {
  function _hostnameOf(url) {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }
  function _channelOf(host) {
    if (!host) return null
    const tiers = []
    for (const name of channelNames) {
      for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
        const h = _hostnameOf(url)
        if (h) tiers.push({ name, h })
      }
    }
    tiers.sort((a, b) => b.h.length - a.h.length)
    // Two channels can serve ONE host: `demo` and `production` share the prod
    // preview host (and `demo-dev`/`development` the dev one). First-in-
    // registry-order named `demo` for every prod preview page, so its shell
    // links went to demo.symbols.app. The registry's own collision rule
    // (`channelByApiUrl` — a real plane outranks its demo twin) breaks the tie.
    const pick = (names) =>
      names.find((n) => channelByApiUrl(resolveChannel(n).api) === n) || names[0]
    const exact = tiers.filter(({ h }) => host === h)
    if (exact.length) return pick(exact.map(({ name }) => name))
    const suffix = tiers.filter(({ h }) => host.endsWith('.' + h))
    if (suffix.length) {
      const best = suffix[0].h.length
      return pick(suffix.filter(({ h }) => h.length === best).map(({ name }) => name))
    }
    return null
  }
  function _isLocalHost(host) {
    return !!host && (host === 'localhost' || host.endsWith('.localhost'))
  }
  function _localOrigin(hostname) {
    if (typeof window === 'undefined') return `http://${hostname}:1355`
    const port = window.location.port ? `:${window.location.port}` : ''
    return `${window.location.protocol}//${hostname}${port}`
  }

  if (typeof window === 'undefined') return 'https://my.symbols.app'
  const host = window.location.hostname
  if (_isLocalHost(host)) {
    if (host === 'workspace.localhost' || host === 'my.localhost') {
      return _localOrigin(host)
    }
    return _localOrigin('workspace.localhost')
  }
  for (const name of channelNames) {
    if (host === _hostnameOf(channelShellUrl(name))) {
      return `${window.location.protocol}//${host}`
    }
  }
  const channel = _channelOf(host)
  const shell = channel ? channelShellUrl(channel) : null
  return shell || 'https://my.symbols.app'
}

export const canvasUrl = (path = '/') => {
  // FA204 / frank trap — self-contained inline copy of canvasOrigin (= the
  // channel's workspace shell), see the file header. Kept identical.
  function _canvasOrigin() {
    function _hostnameOf(url) {
      if (!url) return null
      try {
        return new URL(url).hostname
      } catch {
        return null
      }
    }
    function _channelOf(host) {
      if (!host) return null
      const tiers = []
      for (const name of channelNames) {
        for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
          const h = _hostnameOf(url)
          if (h) tiers.push({ name, h })
        }
      }
      tiers.sort((a, b) => b.h.length - a.h.length)
      // Two channels can serve ONE host: `demo` and `production` share the prod
      // preview host (and `demo-dev`/`development` the dev one). First-in-
      // registry-order named `demo` for every prod preview page, so its shell
      // links went to demo.symbols.app. The registry's own collision rule
      // (`channelByApiUrl` — a real plane outranks its demo twin) breaks the tie.
      const pick = (names) =>
        names.find((n) => channelByApiUrl(resolveChannel(n).api) === n) || names[0]
      const exact = tiers.filter(({ h }) => host === h)
      if (exact.length) return pick(exact.map(({ name }) => name))
      const suffix = tiers.filter(({ h }) => host.endsWith('.' + h))
      if (suffix.length) {
        const best = suffix[0].h.length
        return pick(suffix.filter(({ h }) => h.length === best).map(({ name }) => name))
      }
      return null
    }
    function _isLocalHost(host) {
      return !!host && (host === 'localhost' || host.endsWith('.localhost'))
    }
    function _localOrigin(hostname) {
      if (typeof window === 'undefined') return `http://${hostname}:1355`
      const port = window.location.port ? `:${window.location.port}` : ''
      return `${window.location.protocol}//${hostname}${port}`
    }

    if (typeof window === 'undefined') return 'https://my.symbols.app'
    const host = window.location.hostname
    if (_isLocalHost(host)) {
      if (host === 'workspace.localhost' || host === 'my.localhost') {
        return _localOrigin(host)
      }
      return _localOrigin('workspace.localhost')
    }
    for (const name of channelNames) {
      if (host === _hostnameOf(channelShellUrl(name))) {
        return `${window.location.protocol}//${host}`
      }
    }
    const channel = _channelOf(host)
    const shell = channel ? channelShellUrl(channel) : null
    return shell || 'https://my.symbols.app'
  }

  const p = path.startsWith('/') ? path : `/${path}`
  return `${_canvasOrigin()}${p}`
}

export const workspaceOrigin = () => {
  function _hostnameOf(url) {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }
  function _channelOf(host) {
    if (!host) return null
    const tiers = []
    for (const name of channelNames) {
      for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
        const h = _hostnameOf(url)
        if (h) tiers.push({ name, h })
      }
    }
    tiers.sort((a, b) => b.h.length - a.h.length)
    // Two channels can serve ONE host: `demo` and `production` share the prod
    // preview host (and `demo-dev`/`development` the dev one). First-in-
    // registry-order named `demo` for every prod preview page, so its shell
    // links went to demo.symbols.app. The registry's own collision rule
    // (`channelByApiUrl` — a real plane outranks its demo twin) breaks the tie.
    const pick = (names) =>
      names.find((n) => channelByApiUrl(resolveChannel(n).api) === n) || names[0]
    const exact = tiers.filter(({ h }) => host === h)
    if (exact.length) return pick(exact.map(({ name }) => name))
    const suffix = tiers.filter(({ h }) => host.endsWith('.' + h))
    if (suffix.length) {
      const best = suffix[0].h.length
      return pick(suffix.filter(({ h }) => h.length === best).map(({ name }) => name))
    }
    return null
  }
  function _isLocalHost(host) {
    return !!host && (host === 'localhost' || host.endsWith('.localhost'))
  }
  function _localOrigin(hostname) {
    if (typeof window === 'undefined') return `http://${hostname}:1355`
    const port = window.location.port ? `:${window.location.port}` : ''
    return `${window.location.protocol}//${hostname}${port}`
  }

  if (typeof window === 'undefined') return 'https://my.symbols.app'
  const host = window.location.hostname
  if (_isLocalHost(host)) {
    // The local dev shell actually runs on `workspace.localhost`.
    // `my.localhost` is only the channels registry's NOMINAL local shell host
    // and normally has no app registered against it, so returning it hardcoded
    // sent every deep-link into the shell — including the post-create redirect
    // after "New project" — to a portless "No app registered for my.localhost"
    // 404. `isInsideWorkspaceShell` below already treats BOTH names as the
    // shell, which is what made the mismatch invisible.
    if (host === 'workspace.localhost' || host === 'my.localhost') {
      return _localOrigin(host)
    }
    return _localOrigin('workspace.localhost')
  }
  // Already on the shell host — keep it (preserves protocol + port).
  for (const name of channelNames) {
    if (host === _hostnameOf(channelShellUrl(name))) {
      return `${window.location.protocol}//${host}`
    }
  }
  const channel = _channelOf(host)
  const shell = channel ? channelShellUrl(channel) : null
  return shell || 'https://my.symbols.app'
}

export const workspaceUrl = (path = '/') => {
  // FA204 / frank trap — self-contained inline copy of workspaceOrigin, see
  // the file header. Kept identical to the export above.
  function _workspaceOrigin() {
    function _hostnameOf(url) {
      if (!url) return null
      try {
        return new URL(url).hostname
      } catch {
        return null
      }
    }
    function _channelOf(host) {
      if (!host) return null
      const tiers = []
      for (const name of channelNames) {
        for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
          const h = _hostnameOf(url)
          if (h) tiers.push({ name, h })
        }
      }
      tiers.sort((a, b) => b.h.length - a.h.length)
      // Two channels can serve ONE host: `demo` and `production` share the prod
      // preview host (and `demo-dev`/`development` the dev one). First-in-
      // registry-order named `demo` for every prod preview page, so its shell
      // links went to demo.symbols.app. The registry's own collision rule
      // (`channelByApiUrl` — a real plane outranks its demo twin) breaks the tie.
      const pick = (names) =>
        names.find((n) => channelByApiUrl(resolveChannel(n).api) === n) || names[0]
      const exact = tiers.filter(({ h }) => host === h)
      if (exact.length) return pick(exact.map(({ name }) => name))
      const suffix = tiers.filter(({ h }) => host.endsWith('.' + h))
      if (suffix.length) {
        const best = suffix[0].h.length
        return pick(suffix.filter(({ h }) => h.length === best).map(({ name }) => name))
      }
      return null
    }
    function _isLocalHost(host) {
      return !!host && (host === 'localhost' || host.endsWith('.localhost'))
    }
    function _localOrigin(hostname) {
      if (typeof window === 'undefined') return `http://${hostname}:1355`
      const port = window.location.port ? `:${window.location.port}` : ''
      return `${window.location.protocol}//${hostname}${port}`
    }

    if (typeof window === 'undefined') return 'https://my.symbols.app'
    const host = window.location.hostname
    if (_isLocalHost(host)) {
      if (host === 'workspace.localhost' || host === 'my.localhost') {
        return _localOrigin(host)
      }
      return _localOrigin('workspace.localhost')
    }
    for (const name of channelNames) {
      if (host === _hostnameOf(channelShellUrl(name))) {
        return `${window.location.protocol}//${host}`
      }
    }
    const channel = _channelOf(host)
    const shell = channel ? channelShellUrl(channel) : null
    return shell || 'https://my.symbols.app'
  }

  const p = path.startsWith('/') ? path : `/${path}`
  return `${_workspaceOrigin()}${p}`
}

// True when the current browser host IS the workspace shell — i.e. the
// surface is running INSIDE the unified workspace app (which mounts canvas
// at the same origin via the SPA router), NOT standalone at a public
// preview host. Lets "open in canvas editor" affordances route in-app
// (`el.router('/<owner>/<key>')`) instead of opening a new tab when the
// preview is embedded in the workspace, while still opening the canvas
// URL in a tab when the preview runs on its own at `*.preview.symbols.app`.
//
// Shell hosts: the channel registry's `<tier>.my.symbols.app` (prod:
// `my.symbols.app`) plus the local dev shell. Note the workspace dev
// server runs on `workspace.localhost` (the channels `local` shell host
// is `my.localhost`); both count. A standalone preview host never carries
// the `.my.` shell segment and is never `workspace.localhost`, so it
// returns false and keeps its new-tab behaviour.
export const isInsideWorkspaceShell = () => {
  function _hostnameOf(url) {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }

  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  if (host === 'workspace.localhost' || host === 'my.localhost') return true
  for (const name of channelNames) {
    if (host === _hostnameOf(channelShellUrl(name))) return true
  }
  return false
}
